import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { Key, Plus, Trash2, Copy, Check, AlertTriangle, BookOpen, ChevronDown, ChevronRight } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useConfirm } from "../components/ConfirmProvider";
import { listarApiKeys, gerarApiKey, revogarApiKey } from "../services/apiKeys";
import { copiarTexto } from "../utils/clipboard";

function mascararChave(chave: string): string {
  // Mostra prefixo + 4 últimos chars. Resto vira "…".
  if (!chave || chave.length < 16) return chave;
  const prefixo = chave.slice(0, 12); // "gs_live_xxxx"
  const sufixo = chave.slice(-4);
  return `${prefixo}…${sufixo}`;
}

export function Api() {
  const { can } = useAuth();
  if (!can(["admin", "gestor"])) return <Navigate to="/" replace />;
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [chaveNova, setChaveNova] = useState<string | null>(null);
  const [copiouNova, setCopiouNova] = useState(false);

  const { data: keys = [], isLoading } = useQuery({ queryKey: ["api-keys"], queryFn: listarApiKeys });

  const gerarM = useMutation({
    mutationFn: gerarApiKey,
    onSuccess: (r) => {
      setChaveNova(r.chave);
      qc.invalidateQueries({ queryKey: ["api-keys"] });
    },
  });
  const revogarM = useMutation({
    mutationFn: revogarApiKey,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-keys"] }),
  });

  async function aoRevogar(id: string) {
    const ok = await confirm({
      title: "Revogar API Key?",
      message: "Integrações usando essa chave vão parar de funcionar imediatamente. Esta ação não pode ser desfeita.",
      danger: true,
      confirmText: "Revogar",
    });
    if (ok) revogarM.mutate(id);
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100 inline-flex items-center gap-2">
          <Key size={20} /> API
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Chaves de integração pra ferramentas externas — automação de tarefas, sincronização financeira, scripts internos.
          A chave herda as permissões do seu usuário (admin/gestor).
        </p>
      </div>

      {/* Chave recém-gerada — mostra 1x, depois some */}
      {chaveNova && (
        <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-300 dark:border-emerald-700 rounded-xl p-4 space-y-2">
          <div className="flex items-start gap-2">
            <AlertTriangle size={18} className="text-emerald-700 dark:text-emerald-300 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-emerald-900 dark:text-emerald-100 text-sm">Chave gerada — guarde agora!</p>
              <p className="text-xs text-emerald-800 dark:text-emerald-200">
                Esta é a única vez que a chave completa fica visível. Depois de fechar, apenas o prefixo aparece.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setChaveNova(null)}
              className="text-xs text-emerald-700 hover:underline"
            >
              Fechar
            </button>
          </div>
          <div className="flex items-center gap-2 bg-white dark:bg-slate-900 border border-emerald-200 dark:border-emerald-700 rounded p-2">
            <code className="font-mono text-xs flex-1 truncate text-slate-800 dark:text-slate-100">{chaveNova}</code>
            <button
              type="button"
              onClick={async () => {
                if (chaveNova) {
                  const ok = await copiarTexto(chaveNova);
                  if (ok) { setCopiouNova(true); setTimeout(() => setCopiouNova(false), 1500); }
                }
              }}
              className="inline-flex items-center gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 rounded"
            >
              {copiouNova ? <><Check size={12} /> Copiado</> : <><Copy size={12} /> Copiar</>}
            </button>
          </div>
        </div>
      )}

      {/* Lista */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Chaves ativas</h2>
          <button
            type="button"
            onClick={() => gerarM.mutate()}
            disabled={gerarM.isPending}
            className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 rounded-lg font-medium disabled:opacity-50"
          >
            <Plus size={14} /> {gerarM.isPending ? "Gerando…" : "Gerar nova chave"}
          </button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 uppercase text-xs">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Chave</th>
              <th className="text-left px-4 py-2 font-medium">Criada</th>
              <th className="text-left px-4 py-2 font-medium">Último uso</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="text-slate-700 dark:text-slate-200">
            {isLoading && (<tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">Carregando…</td></tr>)}
            {!isLoading && keys.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">Nenhuma chave ativa. Gere uma pra começar.</td></tr>
            )}
            {keys.map((k) => (
              <tr key={k.id} className="border-t border-slate-100 dark:border-slate-800">
                <td className="px-4 py-2 font-mono text-xs">{mascararChave(k.chave)}</td>
                <td className="px-4 py-2 text-slate-500">{new Date(k.criadoEm).toLocaleString("pt-BR")}</td>
                <td className="px-4 py-2 text-slate-500">{k.usadoEm ? new Date(k.usadoEm).toLocaleString("pt-BR") : <span className="text-slate-400 italic">nunca</span>}</td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => aoRevogar(k.id)}
                    disabled={revogarM.isPending}
                    className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                    title="Revogar"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DocsSection />
    </div>
  );
}

function DocsSection() {
  const [aberta, setAberta] = useState(true);
  const base = `${window.location.origin}/api`;

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setAberta((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
      >
        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 inline-flex items-center gap-2">
          <BookOpen size={16} /> Documentação rápida
        </h2>
        {aberta ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      {aberta && (
        <div className="px-4 pb-4 space-y-4 text-sm text-slate-700 dark:text-slate-200">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Todas as requisições usam <code className="font-mono bg-slate-100 dark:bg-slate-800 px-1 rounded">Authorization: Bearer SUA_CHAVE</code> e <code className="font-mono bg-slate-100 dark:bg-slate-800 px-1 rounded">Content-Type: application/json</code>.
            Base URL: <code className="font-mono bg-slate-100 dark:bg-slate-800 px-1 rounded">{base}</code>
          </p>

          <Bloco titulo="Criar tarefa avulsa">
{`curl -X POST ${base}/tarefas \\
  -H "Authorization: Bearer gs_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "titulo": "Comprar domínio do cliente X",
    "descricao": "Renovar até 30/06",
    "atribuidoA": "<usuarioId>",
    "prazo": "2026-06-30",
    "prioridade": "media"
  }'`}
          </Bloco>

          <Bloco titulo="Criar tarefa vinculada a um projeto">
{`curl -X POST ${base}/tarefas \\
  -H "Authorization: Bearer gs_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "titulo": "Subir landing page",
    "atribuidoA": "<usuarioId>",
    "projetoId": "<projetoId>",
    "prazo": "2026-06-15",
    "prioridade": "alta"
  }'`}
          </Bloco>

          <Bloco titulo="Listar parcelas pendentes">
{`curl ${base}/financeiro/parcelas?status=pendente \\
  -H "Authorization: Bearer gs_live_..."`}
          </Bloco>

          <Bloco titulo="Marcar parcela como paga">
{`curl -X POST ${base}/financeiro/parcelas/<parcelaId>/pagamento \\
  -H "Authorization: Bearer gs_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "dataPagamento": "2026-05-18",
    "valorPago": 1500.00,
    "metodoPagamento": "pix",
    "observacao": "Liquidado via integração XYZ"
  }'`}
          </Bloco>

          <Bloco titulo="Estornar pagamento">
{`curl -X POST ${base}/financeiro/parcelas/<parcelaId>/estornar \\
  -H "Authorization: Bearer gs_live_..."`}
          </Bloco>

          <Bloco titulo="Buscar projetos (pra descobrir o projetoId)">
{`curl "${base}/projetos?perPage=50" \\
  -H "Authorization: Bearer gs_live_..."`}
          </Bloco>

          <Bloco titulo="Buscar usuários (pra descobrir atribuidoA)">
{`curl ${base}/usuarios \\
  -H "Authorization: Bearer gs_live_..."`}
          </Bloco>

          <p className="text-xs text-slate-500 dark:text-slate-400 pt-2 border-t border-slate-200 dark:border-slate-700">
            Dica: a chave herda <strong>permissões do usuário que gerou</strong>. Se gestor gerou, ações financeiras
            funcionam; se funcionário gerou, só tarefas funcionam. Revogar não pode ser desfeito — gere uma nova.
          </p>
        </div>
      )}
    </div>
  );
}

function Bloco({ titulo, children }: { titulo: string; children: string }) {
  const [copiou, setCopiou] = useState(false);
  async function copiar() {
    const ok = await copiarTexto(children);
    if (ok) { setCopiou(true); setTimeout(() => setCopiou(false), 1500); }
  }
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">{titulo}</p>
        <button
          type="button"
          onClick={copiar}
          className="text-[10px] text-slate-500 hover:text-blue-600 inline-flex items-center gap-1"
        >
          {copiou ? <><Check size={10} /> copiado</> : <><Copy size={10} /> copiar</>}
        </button>
      </div>
      <pre className="bg-slate-900 dark:bg-slate-950 text-slate-100 text-[11px] font-mono p-3 rounded-lg overflow-x-auto leading-relaxed">{children}</pre>
    </div>
  );
}
